import type { ApplicableContext } from '@cotera/client/v0/actions/framework';
import type { WebhookInstallStatus } from '@cotera/client/app/v1/webhook-providers/types';
import type { WorkflowGraphApiData } from './workflow-graph.types';
import { WorkflowWebhookTriggerFullNodeModel } from './workflow-node-models/workflow-webhook-trigger-full-node.model';
import { WorkflowWebhookTriggerPreviewNodeModel } from './workflow-node-models/workflow-webhook-trigger-preview-node.model';
import { WorkflowCronTriggerNodeModel } from './workflow-node-models/workflow-cron-trigger-node.model';
import { WorkflowEventTriggerNodeModel } from './workflow-node-models/workflow-event-trigger-node.model';
import { WorkflowDatasetTriggerNodeModel } from './workflow-node-models/workflow-dataset-trigger-node.model';
import type { WorkflowWebhookTriggerNodeModel } from './workflow-node-models/webhook-trigger-node.types';
import type { WebhookTriggerInstallState } from './workflow-node-models/types';
import { readWebhookInstallStatusFromGraph } from './workflow-node-models/webhook-trigger-graph';
import { selectionHasWorkflowNode } from './workflow-editor-toolbar-state';
import { WorkflowModel } from './workflow.model';

export const TEST_WEBHOOK_ACTIVE_TITLE =
  'Send a simulated provider request through this webhook';

export const TRIGGER_CRON_NOW_ACTIVE_TITLE =
  'Run this cron trigger immediately, even when the schedule is disabled';

export const TRIGGER_EVENT_NOW_ACTIVE_TITLE =
  'Fire this event trigger with a sample payload to test the workflow';

export const TEST_DATASET_TRIGGER_ACTIVE_TITLE =
  'Open the dataset to run the workflow against its rows';

/**
 * The workflow every canvas action acts on.
 *
 * Prefers the *targeted* model over the first one in scope, and the difference is
 * load-bearing. Several workflows share one actions scope — `ModelScopeFactory`
 * registers into the existing scope rather than opening a child one — and
 * `getInScopeModelOfType` returns the first registered of a type. So on a nested
 * workflow page, where the parent registered first, it hands back the *parent*:
 * duplicate and delete read the parent's selection and never appeared, and the
 * trigger actions would have fired the parent workflow.
 *
 * Targeting is the mechanism already built for this. `WorkflowModelProvider` calls
 * `targetModel` only for the active artifact, and `WorkflowModelStackAwareProvider`
 * grants that to the workflow the pushed graph view names — which is the nested one.
 *
 * The in-scope fallback keeps every surface where nothing is targeted behaving as
 * it did, so this can only resolve better, never worse.
 */
export function workflowModelFromContext(
  context: ApplicableContext
): WorkflowModel | null {
  return (
    context.targetedModelOfType(WorkflowModel) ??
    context.getInScopeModelOfType(WorkflowModel)
  );
}

/**
 * The workflow whose editor a trigger button was pressed in.
 *
 * Firing a trigger fans out to every workflow bound to it, which is what a real
 * delivery means and not what a button sitting on one graph promises — so these
 * actions name this workflow and the server narrows to it. The dataset grid's
 * per-workflow Run button has always done this; the editor's buttons did not,
 * and quietly started the neighbours' runs.
 *
 * Null when no workflow model is in scope. Callers read that as "cannot fire"
 * rather than falling back to an unnarrowed call — running everything is
 * precisely the outcome this exists to prevent.
 */
export function targetWorkflowIdFromContext(
  context: ApplicableContext
): string | null {
  return workflowModelFromContext(context)?.workflowId ?? null;
}

export function targetedWebhookTriggerNodeModel(
  context: ApplicableContext
): WorkflowWebhookTriggerNodeModel | null {
  const full = context.targetedModelOfType(WorkflowWebhookTriggerFullNodeModel);
  if (full !== null) {
    return full;
  }
  return context.targetedModelOfType(WorkflowWebhookTriggerPreviewNodeModel);
}

export function targetedCronTriggerNodeModel(
  context: ApplicableContext
): WorkflowCronTriggerNodeModel | null {
  return context.targetedModelOfType(WorkflowCronTriggerNodeModel);
}

export function targetedEventTriggerNodeModel(
  context: ApplicableContext
): WorkflowEventTriggerNodeModel | null {
  return context.targetedModelOfType(WorkflowEventTriggerNodeModel);
}

export function targetedDatasetTriggerNodeModel(
  context: ApplicableContext
): WorkflowDatasetTriggerNodeModel | null {
  return context.targetedModelOfType(WorkflowDatasetTriggerNodeModel);
}

export function resolveEffectiveWebhookInstallStatus(
  live: WebhookTriggerInstallState,
  graph: WorkflowGraphApiData | null,
  triggerId: string
): WebhookInstallStatus {
  if (live.loaded) {
    return live.installStatus;
  }
  if (graph === null) {
    return live.installStatus;
  }
  return (
    readWebhookInstallStatusFromGraph(graph, triggerId) ?? live.installStatus
  );
}

export function graphHasEnabledTriggerBinding(
  graph: WorkflowGraphApiData | null
): boolean {
  if (graph === null) {
    return false;
  }
  return graph.triggerBindings.some((binding) => binding.enabled === true);
}

export function selectionHasDeletableItems(
  context: ApplicableContext
): boolean {
  const model = workflowModelFromContext(context);
  if (model === null) {
    return false;
  }
  const selection = model.editorSelectionWatchable.snapshot();
  return (
    selection.selectedNodeIds.length > 0 || selection.selectedEdgeIds.length > 0
  );
}

export function workflowCanvasHasNodeSelection(
  context: ApplicableContext
): boolean {
  const model = workflowModelFromContext(context);
  if (model === null) {
    return false;
  }
  return selectionHasWorkflowNode(model.editorSelectionWatchable.snapshot());
}

/**
 * What a canvas clipboard action needs to know about the rest of the app.
 *
 * Only the modal stack, and only for the keyboard bindings: it is a
 * provider-scoped store, so an action reaches it through the component that
 * built it rather than reading a global.
 */
export type WorkflowClipboardOps = {
  /** True while a dialog is stacked over the canvas. */
  isModalOpen: () => boolean;
};

/** Whatever the user is typing in owns the keystroke. */
function focusIsEditable(): boolean {
  const target = document.activeElement;
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  const tagName = target.tagName;
  return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
}

/** Text highlighted anywhere on the page — Cmd-C belongs to the browser then. */
export function hasTextSelection(): boolean {
  const selection = window.getSelection();
  if (selection === null) {
    return false;
  }
  return selection.toString().trim() !== '';
}

/**
 * Whether a canvas shortcut may claim the keystroke.
 *
 * The node inspector floats over the canvas with its own inputs and rich-text
 * editors, and dialogs stack over both — a canvas-level Cmd-C would otherwise
 * steal the copy out of a half-written prompt, and Cmd-D behind the open
 * add-node picker would duplicate a node the user cannot see. The modal check
 * is not redundant with the editable one: focus inside a dialog is usually a
 * button, not a field.
 *
 * Gates the binding only, never `applicable` — the command palette is itself a
 * modal, and these actions have to stay runnable from it.
 */
export function workflowCanvasShortcutAvailable(
  ops: WorkflowClipboardOps
): boolean {
  return ops.isModalOpen() === false && focusIsEditable() === false;
}
